import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { ListenButton } from "./ListenButton";
import { formatReadTime } from "../lib/readingTime";

const HERO = "/images/story/when-luxury-hero.jpg";
const SUPPORTING = "https://customer-assets.emergentagent.com/job_human-flourish-hub/artifacts/7ysurpdb_Unnamed%20album%20-%20002.jpeg";

export default function Story() {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const books = t("story.books", { returnObjects: true }) || [];
  const narration = [
    `${t("story.title")}, ${t("story.author")}.`,
    t("story.opening"), t("story.p1"), t("story.p2"), t("story.p3"), t("story.p4"), t("story.p5")
  ].join(" ");
  // Main body only: opening + 5 paragraphs + the "what stayed with me" reflection.
  const readTimeLabel = formatReadTime(
    [t("story.opening"), t("story.p1"), t("story.p2"), t("story.p3"), t("story.p4"), t("story.p5"), t("story.whatStayedBody")],
    t
  );

  return (
    <div className="pb-24" data-testid="story-page">

      <div className="max-w-5xl mx-auto px-6 pt-10">
        <Link to="/journal" data-testid="story-back-link" className="inline-flex items-center space-x-2 text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>{t("story.back")}</span>
        </Link>
      </div>

      <header className="max-w-3xl mx-auto px-6 pt-10 pb-14 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("story.category")}</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">{t("story.title")}</h1>
        <div className="flex items-center justify-center space-x-4 text-xs font-sans text-secondary-custom uppercase tracking-wider">
          <span>{t("story.author")}</span><span>•</span><span data-testid="story-read-time">{readTimeLabel}</span>
        </div>
        <div className="flex justify-center pt-2">
          <ListenButton text={narration} testId="story-page-listen" idleKey="listen.toStory" />
        </div>
      </header>

      <figure className="w-full">
        <div className="w-full max-h-[80vh] overflow-hidden border-y border-subtle-custom">
          <img src={HERO} alt={t("story.title")} className="w-full h-full object-cover" data-testid="story-hero-image" />
        </div>
        <figcaption className="max-w-3xl mx-auto px-6 pt-4 text-center font-serif text-xs sm:text-sm italic text-secondary-custom leading-relaxed">{t("story.heroCaption")}</figcaption>
      </figure>

      <article className="max-w-2xl mx-auto px-6 pt-16 space-y-8 font-serif text-lg sm:text-xl text-primary-custom leading-relaxed font-light">
        <p className="text-2xl sm:text-3xl leading-snug text-primary-custom">{t("story.opening")}</p>
        <p className="first-letter:text-5xl first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:font-medium first-letter:text-[var(--accent-bronze)]">{t("story.p1")}</p>
        <p>{t("story.p2")}</p>
        <p>{t("story.p3")}</p>
      </article>

      <figure className="max-w-3xl mx-auto px-6 py-16">
        <div className="border border-subtle-custom bg-primary-custom p-3 sm:p-4 shadow-sm max-w-md mx-auto">
          <img src={SUPPORTING} alt={t("story.title")} className="w-full h-auto" loading="lazy" data-testid="story-supporting-image" />
        </div>
        <figcaption className="max-w-md mx-auto pt-4 text-center font-serif text-xs sm:text-sm italic text-secondary-custom leading-relaxed">{t("story.supportingCaption")}</figcaption>
      </figure>

      <article className="max-w-2xl mx-auto px-6 space-y-8 font-serif text-lg sm:text-xl text-primary-custom leading-relaxed font-light">
        <p>{t("story.p4")}</p>
        <p>{t("story.p5")}</p>
      </article>

      <section className="max-w-3xl mx-auto px-6 mt-20" data-testid="story-what-stayed">
        <div className="border-y border-subtle-custom py-12 space-y-6 text-center">
          <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("story.whatStayedEyebrow")}</span>
          <p className="font-serif text-xl sm:text-2xl italic text-primary-custom leading-relaxed font-light">{t("story.whatStayedBody")}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 mt-20 space-y-8" data-testid="story-suggested-reading">
        <div className="flex items-center space-x-3">
          <BookOpen className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1.5} />
          <h2 className="text-[10px] font-sans tracking-[0.3em] uppercase text-primary-custom font-semibold">{t("story.suggestedReading")}</h2>
        </div>
        <div className="space-y-6">
          {books.map((book, idx) => (
            <div key={idx} className="border-t border-subtle-custom pt-6 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
              <div className="space-y-1">
                <span className="font-serif text-lg text-primary-custom italic">{book.title}</span>
                <span className="font-sans text-xs text-secondary-custom uppercase tracking-wider block">{book.author}</span>
              </div>
              <p className="font-serif text-sm text-secondary-custom sm:max-w-xs sm:text-right leading-relaxed">{book.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 mt-20" data-testid="story-related">
        <div className="border border-subtle-custom bg-surface-custom/30 p-10 text-center space-y-6">
          <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("story.relatedEyebrow")}</span>
          <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light max-w-xl mx-auto">{t("story.relatedBody")}</p>
          <Link to="/journal" data-testid="story-related-link" className="inline-flex items-center space-x-2 text-xs font-sans tracking-[0.2em] uppercase text-primary-custom font-semibold hover:text-[var(--accent-bronze)] transition-colors border-b border-primary pb-1">
            <span>{t("story.returnJournal")}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

    </div>
  );
}
